// accordion-custom.js
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

class AccordionCustom extends HTMLElement {
  // Tuning (premium defaults)
  static OPEN_DURATION = 520;
  static CLOSE_DURATION = 440;
  static OPEN_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
  static CLOSE_EASING = 'cubic-bezier(0.4, 0, 1, 1)';
  static HANDOFF_DELAY_MS = 20;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement | null} */
  get accordionGroup() {
    return this.closest('.accordion');
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }

  #controller = new AbortController();
  /** @type {WeakMap<HTMLDetailsElement, Animation>} */
  #contentAnimations = new WeakMap();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#enforceSingleOpen();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.#handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLDetailsElement[]}
   */
  #getDetailsInGroup(root) {
    return Array.from(root.querySelectorAll('accordion-custom > details')).filter(
      (details) => details instanceof HTMLDetailsElement
    );
  }

  /**
   * @param {HTMLDetailsElement} details
   * @returns {HTMLElement | null}
   */
  #getContent(details) {
    const el = details.querySelector('.details-content');
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #cancelAnimation(details) {
    const anim = this.#contentAnimations.get(details);
    if (anim) anim.cancel();
    this.#contentAnimations.delete(details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #cleanupContentInlineStyles(details) {
    const content = this.#getContent(details);
    if (!content) return;
    content.style.removeProperty('height');
    content.style.removeProperty('overflow');
    content.style.removeProperty('overflow-x');
    content.style.removeProperty('overflow-y');
  }

  /**
   * Handles the click event.
   * @param {MouseEvent} event
   */
  #handleClick = async (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    const group = this.accordionGroup;

    if (!group) return;

    event.preventDefault();

    const allDetails = this.#getDetailsInGroup(group);
    const target = this.details;
    const currentlyOpen = allDetails.find((d) => d.open && d !== target) ?? null;

    if (this.#prefersReducedMotion) {
      if (target.open) {
        target.open = false;
      } else {
        if (currentlyOpen) currentlyOpen.open = false;
        target.open = true;
      }
      return;
    }

    // If target is open: smooth close
    if (target.open) {
      await this.#animateClose(target);
      return;
    }

    // Switch: close & open with overlap (handoff without "pause")
    const closePromise = currentlyOpen ? this.#animateClose(currentlyOpen) : Promise.resolve();
    // Tiny delay (0–20ms) feels most continuous; next frame avoids layout jank on some devices.
    await this.#nextFrame();
    if (AccordionCustom.HANDOFF_DELAY_MS > 0) await this.#delay(AccordionCustom.HANDOFF_DELAY_MS);
    const openPromise = this.#animateOpen(target);

    await Promise.all([closePromise, openPromise]);
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#cancelAnimation(this.details);
    this.#setDefaultOpenState();
    this.#enforceSingleOpen();
  };

  /**
   * Handles content resize events (e.g., from show-more-component)
   */
  #handleContentResize = () => {
    if (this.details.open) {
      const animation = this.#contentAnimations.get(this.details);
      if (animation && animation.playState === 'running') {
        // Update animation if it's currently running
        this.#updateAnimationHeight(this.details);
      }
    }
  };

  /**
   * Updates the animation height for an open accordion when content size changes
   * @param {HTMLDetailsElement} details
   */
  #updateAnimationHeight(details) {
    const content = this.#getContent(details);
    if (!content) return;

    const animation = this.#contentAnimations.get(details);
    if (!animation || animation.playState !== 'running') return;

    // Get current height and new target height
    const currentHeight = content.getBoundingClientRect().height;
    const targetHeight = content.scrollHeight;

    // If heights are different, update the animation
    if (Math.abs(currentHeight - targetHeight) > 1) {
      // Cancel current animation
      animation.cancel();
      this.#contentAnimations.delete(details);

      // Start new animation from current height to new target
      content.style.height = `${currentHeight}px`;
      content.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        const newAnimation = content.animate(
          [{ height: `${currentHeight}px` }, { height: `${targetHeight}px` }],
          {
            duration: AccordionCustom.OPEN_DURATION,
            easing: AccordionCustom.OPEN_EASING,
            fill: 'forwards',
          }
        );

        this.#contentAnimations.set(details, newAnimation);

        newAnimation.finished
          .catch(() => {})
          .then(() => {
            this.#contentAnimations.delete(details);
            this.#cleanupContentInlineStyles(details);
          });
      });
    }
  }

  /**
   * @param {number} ms
   * @returns {Promise<void>}
   */
  #delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  /**
   * @returns {Promise<void>}
   */
  #nextFrame() {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  /**
   * @param {HTMLDetailsElement} details
   * @returns {Promise<void>}
   */
  async #animateOpen(details) {
    const content = this.#getContent(details);
    if (!content) {
      details.open = true;
      return;
    }

    this.#cancelAnimation(details);

    // Ensure content exists in DOM
    details.open = true;

    content.style.height = '0px';
    content.style.overflow = 'hidden';

    await this.#nextFrame();

    const targetHeight = content.scrollHeight;

    const animation = content.animate(
      [{ height: '0px' }, { height: `${targetHeight}px` }],
      {
        duration: AccordionCustom.OPEN_DURATION,
        easing: AccordionCustom.OPEN_EASING,
        fill: 'forwards',
      }
    );

    this.#contentAnimations.set(details, animation);

    await animation.finished.catch(() => {});

    this.#contentAnimations.delete(details);
    this.#cleanupContentInlineStyles(details);
  }

  /**
   * @param {HTMLDetailsElement} details
   * @returns {Promise<void>}
   */
  async #animateClose(details) {
    const content = this.#getContent(details);
    if (!content) {
      details.open = false;
      return;
    }

    this.#cancelAnimation(details);

    // Keep content around for the animation
    details.open = true;

    // Use current rendered height (supports mid-animation clicks without jumping)
    const startHeight = content.getBoundingClientRect().height;

    content.style.height = `${startHeight}px`;
    content.style.overflow = 'hidden';

    await this.#nextFrame();

    const animation = content.animate(
      [{ height: `${startHeight}px` }, { height: '0px' }],
      {
        duration: AccordionCustom.CLOSE_DURATION,
        easing: AccordionCustom.CLOSE_EASING,
        fill: 'forwards',
      }
    );

    this.#contentAnimations.set(details, animation);

    await animation.finished.catch(() => {});

    this.#contentAnimations.delete(details);
    details.open = false;
    this.#cleanupContentInlineStyles(details);
  }

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    const shouldBeOpen =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
    this.details.open = shouldBeOpen;
  }

  #enforceSingleOpen() {
    const group = this.accordionGroup;
    if (!group) return;
    const allDetails = Array.from(group.querySelectorAll('accordion-custom > details')).filter(
      (details) => details instanceof HTMLDetailsElement
    );
    const openDetails = allDetails.filter((details) => details.open);
    if (openDetails.length === 0 && allDetails.length > 0) {
      const firstDetail = allDetails[0];
      if (firstDetail instanceof HTMLDetailsElement) {
        firstDetail.open = true;
      }
      return;
    }
    if (openDetails.length > 1) {
      openDetails.forEach((details, index) => {
        if (index > 0) {
          details.open = false;
        }
      });
    }
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.#cancelAnimation(this.details);
      this.details.open = false;
      this.#cleanupContentInlineStyles(this.details);
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}